import { Link as TanStackRouterLink } from '@tanstack/react-router'
import clsx from 'clsx'
import React from 'react'

import { Icon } from '#components/Icon'

import { parseInternalHref } from './helpers'
import type { IProps } from './interfaces'
import styles from './styles.module.scss'

export const Link = ({
  children,
  className,
  displayExternalLinkIcon = true,
  href,
  ref: _ref, // Remove ref property from otherProps as it opens up a world of pain in typing
  rel,
  target,
  withUnderline = true,
  loading = false,
  ariaLabel,
  ...otherProps
}: IProps): React.JSX.Element => {
  const computedClassName = clsx(styles.link, loading && styles.loading, withUnderline && styles.underline, className)

  // KISS: if it contains as column then it is either a port or protocol, so let us behold that as external.
  const isExternal = href && !!href.match(/:/)

  if (isExternal) {
    // If an explicit target was given, use it. If not, use _blank for external links.
    const transformedLinkTarget = target ?? '_blank'

    // If the target is _blank, always use rel="noreferrer". For more info on this, see:
    //  https://html.spec.whatwg.org/multipage/links.html#link-type-noopener
    const transformedLinkRel = transformedLinkTarget === '_blank' ? 'noreferrer' : rel

    return (
      <a
        className={computedClassName}
        href={href}
        rel={transformedLinkRel}
        target={transformedLinkTarget}
        aria-label={ariaLabel}
        {...otherProps}
        onClick={(event): void => event.stopPropagation()}
      >
        {children}
        {displayExternalLinkIcon && <Icon name="external-link" className={styles.externalLinkIcon} />}
      </a>
    )
  }

  if (!href) {
    // If we use a Link component for onPress only it does not have a href. In this case we don't want to
    // render the TanStack Router <Link> component, but just an Anchor element. If you run into this case, you
    // might want to use a button instead of our Link component.

    return (
      <a className={computedClassName} aria-label={ariaLabel} {...otherProps}>
        {children}
      </a>
    )
  }

  const { pathname, search } = parseInternalHref(href)

  return (
    <TanStackRouterLink
      className={computedClassName}
      to={pathname}
      search={search}
      aria-label={ariaLabel}
      {...otherProps}
    >
      {children}
    </TanStackRouterLink>
  )
}
